import React, { useEffect, useState } from "react";

import { LoadingButton } from "@mui/lab";
import { Box, Grid, LinearProgress, TextField } from "@mui/material";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { MarketplaceExtractAttribute } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_marketplace_pb";

import { getMarketplaceExtractAttributes } from "api/endpoints/marketplace";
import { callOptionsHeaders } from "api/helpers";
import GenericDialog from "components/shared/generic-dialog";
import { DEFAULT_REGION_CODE } from "constants/index";

export interface NewAttributeGroupFormState {
  name: string;
  attributes: string[];
}

interface AddAttributeGroupFormProps {
  isOpen: boolean;
  handleClose: () => void;
  newAttributeGroupFormState: {
    name: string;
    attributes: string[];
  };
  setNewAttributeGroupFormState: (newState: NewAttributeGroupFormState) => void;
  handleSubmit: () => void;
  isLoading: boolean;
  isDisabled: boolean;
}

type ProductAttributeRow = Pick<
  MarketplaceExtractAttribute,
  "id" | "name" | "description"
>;

const AddAttributeGroupForm = ({
  isOpen,
  handleClose,
  newAttributeGroupFormState,
  setNewAttributeGroupFormState,
  handleSubmit,
  isLoading,
  isDisabled,
}: AddAttributeGroupFormProps) => {
  const [attributeName, setAttributeName] = useState<string[]>([]);
  const [attributesData, setAttributesData] = useState<ProductAttributeRow[]>(
    [],
  );

  const handleAttributesChange = (
    event: SelectChangeEvent<typeof attributeName>,
  ) => {
    const {
      target: { value },
    } = event;

    const selectedOptionStrings =
      typeof value === "string" ? value.split(",") : value;

    const selectedOptions = selectedOptionStrings.map((optionString) => {
      const attribute = attributesData.find((row) => row.name === optionString);
      return attribute ? attribute : null;
    });

    // Filter out any undefined options
    const filteredSelectedOptions = selectedOptions.filter(
      (option) => option !== null,
    ) as ProductAttributeRow[];

    // Extract the IDs from the selected options
    const selectedIds = filteredSelectedOptions.map((option) => option.id);

    // Set state accordingly
    setAttributeName(filteredSelectedOptions.map((option) => option.name));
    setNewAttributeGroupFormState({
      ...newAttributeGroupFormState,
      attributes: selectedIds,
    });
  };

  useEffect(() => {
    const init = async () => {
      const attributesData = await getMarketplaceExtractAttributes(
        callOptionsHeaders(DEFAULT_REGION_CODE),
      );

      setAttributesData(attributesData.attributes);
    };
    init();
  }, []);

  return (
    <GenericDialog
      title="Add Attribute Group"
      width="md"
      hasXCloseBtn
      open={isOpen}
      handleDialogClose={handleClose}
      data-testid="add-attributeGroup-dialog"
    >
      <Grid xs={12} item>
        <form noValidate autoComplete="off">
          <Box
            sx={{ height: "70vh", width: "100%" }}
            display="flex"
            flexDirection="column"
            gap={4}
            marginTop={4}
          >
            <TextField
              required
              label="Name"
              inputProps={{
                "data-testid": "add-attributeGroup-name-field",
              }}
              value={newAttributeGroupFormState.name}
              onChange={(event) =>
                setNewAttributeGroupFormState({
                  ...newAttributeGroupFormState,
                  name: event.target.value,
                })
              }
            />
            <div>
              {isLoading ? (
                <LinearProgress />
              ) : (
                <div>
                  <FormControl sx={{ width: "100%" }}>
                    <InputLabel id="add-attributeGroup-attribute-label">
                      Attributes
                    </InputLabel>
                    <Select
                      labelId="add-attributeGroup-attribute-label"
                      id="add-attributeGroup-attribute-field"
                      multiple
                      value={attributeName}
                      onChange={handleAttributesChange}
                      input={
                        <OutlinedInput
                          id="select-multiple-chip"
                          label="Attributes"
                        />
                      }
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => {
                            const attribute = attributesData.find(
                              (row) => row.name === value,
                            );
                            if (!attribute) return null;
                            return (
                              <Chip key={attribute.id} label={attribute.name} />
                            );
                          })}
                        </Box>
                      )}
                      data-testid="add-attributeGroup-attribute-field"
                    >
                      {attributesData.map((row) => (
                        <MenuItem key={row.id} value={row.name}>
                          {row.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
            </div>
          </Box>
          <LoadingButton
            color="primary"
            onClick={handleSubmit}
            loading={isLoading}
            disabled={isDisabled}
            loadingPosition="center"
            variant="contained"
            data-testid="add-attributeGroup-btn"
          >
            Add Attribute Group
          </LoadingButton>
        </form>
      </Grid>
    </GenericDialog>
  );
};

export default AddAttributeGroupForm;
